import { Injectable } from '@angular/core';
import { BehaviorSubject, map, switchMap } from 'rxjs';
import {
  AssignmentPreview,
  SubmissionResponse,
  SubmissionResult,
  SubmissionQuestion,
  SaveAnswerDto,
  StudentAssignmentStep,
  StudentAssignmentErrorType,
  GRADING_PROGRESS_STEPS,
} from './student-assignment.models';

@Injectable({
  providedIn: 'root',
})
export class StudentAssignmentStateService {
  // ============================================
  // STATE
  // ============================================

  // Current share code
  private readonly shareCode$$ = new BehaviorSubject<string | null>(null);
  readonly shareCode$ = this.shareCode$$.asObservable();

  // Current wizard step
  private readonly currentStep$$ = new BehaviorSubject<StudentAssignmentStep>(
    StudentAssignmentStep.PREVIEW,
  );
  readonly currentStep$ = this.currentStep$$.asObservable();

  // Assignment preview (public data)
  private readonly preview$$ = new BehaviorSubject<AssignmentPreview | null>(
    null,
  );
  readonly preview$ = this.preview$$.asObservable();

  // Current submission
  private readonly submission$$ =
    new BehaviorSubject<SubmissionResponse | null>(null);
  readonly submission$ = this.submission$$.asObservable();

  // Questions (from submission)
  readonly questions$ = this.submission$.pipe(
    map((s) => s?.questions ?? []),
  );

  // Current question index
  private readonly currentQuestionIndex$$ = new BehaviorSubject<number>(0);
  readonly currentQuestionIndex$ =
    this.currentQuestionIndex$$.asObservable();

  // Current question
  readonly currentQuestion$ = this.currentQuestionIndex$.pipe(
    switchMap((index) =>
      this.questions$.pipe(map((questions) => questions[index] ?? null)),
    ),
  );

  // Local answers (for immediate UI updates)
  private readonly localAnswers$$ = new BehaviorSubject<
    Map<string, SaveAnswerDto>
  >(new Map());
  readonly localAnswers$ = this.localAnswers$$.asObservable();

  // Loading states
  private readonly isLoading$$ = new BehaviorSubject<boolean>(false);
  readonly isLoading$ = this.isLoading$$.asObservable();

  private readonly isSubmitting$$ = new BehaviorSubject<boolean>(false);
  readonly isSubmitting$ = this.isSubmitting$$.asObservable();

  private readonly isGrading$$ = new BehaviorSubject<boolean>(false);
  readonly isGrading$ = this.isGrading$$.asObservable();

  // Grading progress step
  private readonly gradingProgressStep$$ = new BehaviorSubject<number>(0);
  readonly gradingProgressStep$ =
    this.gradingProgressStep$$.asObservable();

  // Current grading progress label
  readonly gradingProgressLabel$ = this.gradingProgressStep$.pipe(
    map(
      (step) => GRADING_PROGRESS_STEPS[step]?.label ?? 'Processing...',
    ),
  );

  // Result (after grading)
  private readonly result$$ = new BehaviorSubject<SubmissionResult | null>(
    null,
  );
  readonly result$ = this.result$$.asObservable();

  // Error state
  private readonly error$$ = new BehaviorSubject<string | null>(null);
  readonly error$ = this.error$$.asObservable();

  private readonly errorType$$ =
    new BehaviorSubject<StudentAssignmentErrorType | null>(null);
  readonly errorType$ = this.errorType$$.asObservable();

  // Timer state
  private readonly expiresAt$$ = new BehaviorSubject<Date | null>(null);
  readonly expiresAt$ = this.expiresAt$$.asObservable();

  // ============================================
  // GETTERS
  // ============================================

  get shareCode(): string | null {
    return this.shareCode$$.value;
  }

  get currentStep(): StudentAssignmentStep {
    return this.currentStep$$.value;
  }

  get currentQuestionIndex(): number {
    return this.currentQuestionIndex$$.value;
  }

  get questions(): SubmissionQuestion[] {
    return this.submission$$.value?.questions ?? [];
  }

  get totalQuestions(): number {
    return this.questions.length;
  }

  get isLastQuestion(): boolean {
    return this.currentQuestionIndex$$.value >= this.totalQuestions - 1;
  }

  // ============================================
  // STATE SETTERS (used by sub-services)
  // ============================================

  setShareCode(code: string | null): void {
    this.shareCode$$.next(code);
  }

  setStep(step: StudentAssignmentStep): void {
    this.currentStep$$.next(step);
  }

  setPreview(preview: AssignmentPreview | null): void {
    this.preview$$.next(preview);
  }

  setSubmission(submission: SubmissionResponse | null): void {
    this.submission$$.next(submission);
  }

  setCurrentQuestionIndex(index: number): void {
    this.currentQuestionIndex$$.next(index);
  }

  setLocalAnswers(answers: Map<string, SaveAnswerDto>): void {
    this.localAnswers$$.next(answers);
  }

  getLocalAnswers(): Map<string, SaveAnswerDto> {
    return this.localAnswers$$.value;
  }

  setIsLoading(loading: boolean): void {
    this.isLoading$$.next(loading);
  }

  setIsSubmitting(submitting: boolean): void {
    this.isSubmitting$$.next(submitting);
  }

  setIsGrading(grading: boolean): void {
    this.isGrading$$.next(grading);
  }

  setGradingProgressStep(step: number): void {
    this.gradingProgressStep$$.next(step);
  }

  setResult(result: SubmissionResult | null): void {
    this.result$$.next(result);
  }

  setError(error: string | null): void {
    this.error$$.next(error);
  }

  setErrorType(errorType: StudentAssignmentErrorType | null): void {
    this.errorType$$.next(errorType);
  }

  setExpiresAt(expiresAt: Date | null): void {
    this.expiresAt$$.next(expiresAt);
  }

  // ============================================
  // RESET
  // ============================================

  /**
   * Full reset — clears all state including preview and share code.
   */
  reset(): void {
    this.shareCode$$.next(null);
    this.currentStep$$.next(StudentAssignmentStep.PREVIEW);
    this.preview$$.next(null);
    this.submission$$.next(null);
    this.currentQuestionIndex$$.next(0);
    this.localAnswers$$.next(new Map());
    this.isLoading$$.next(false);
    this.isSubmitting$$.next(false);
    this.isGrading$$.next(false);
    this.gradingProgressStep$$.next(0);
    this.result$$.next(null);
    this.error$$.next(null);
    this.errorType$$.next(null);
    this.expiresAt$$.next(null);
  }

  /**
   * Partial reset for teacher retest flow — clears submission/result/answer
   * state while preserving the preview and share code so the teacher
   * can start a fresh test from the PREVIEW step.
   */
  resetForRetest(): void {
    this.submission$$.next(null);
    this.currentQuestionIndex$$.next(0);
    this.localAnswers$$.next(new Map());
    this.isSubmitting$$.next(false);
    this.isGrading$$.next(false);
    this.gradingProgressStep$$.next(0);
    this.result$$.next(null);
    this.error$$.next(null);
    this.errorType$$.next(null);
    this.expiresAt$$.next(null);
  }
}
